import React, { useEffect } from 'react';
import { Navigate, useRouterState } from '@tanstack/react-router';
import { useAuthStore } from '../../stores/authStore';
import { useDiagramAccess } from '@/hooks/useDiagramAccess';
import { Logger } from '../../utils/logger';
import type { User } from '../../hooks/useAuth';
import { isLogoutRedirectPending } from '../../utils/logoutRedirect';

const logger = Logger.getLoggerWithPrefix('ProtectedRoute');
const SHARE_LINK_ONBOARDING_SESSION_KEY = 'creately:share-link-onboarding';

type GravityJwtPayload = {
  uid?: string;
  email?: string;
  name?: string;
};

function userFromGravityToken(token: string): User {
  const parts = token.split('.');
  const payload = JSON.parse(atob(parts[1])) as GravityJwtPayload;

  return {
    id: payload.uid ?? '',
    email: payload.email ?? '',
    name: payload.name ?? '',
    role: 'user',
    createdAt: new Date(),
  };
}

interface ProtectedRouteProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  redirectToLogin?: boolean;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  fallback,
  redirectToLogin = true
}) => {
  const authenticated = React.useRef(false); // local authenticated state
  const { isAuthenticated, isLoading, setAuth, clearAuth } = useAuthStore();
  const router = useRouterState();
  const path = router.location.pathname;
  const search = router.location.search || '';
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const [linkChecked, setLinkChecked] = React.useState(true);
  const hasAttemptedRefresh = React.useRef(false);

  // Access-link bootstrap: if lt is present, call POST accept (no redirect) and then replace URL without lt
  useEffect(() => {
    (async () => {
      const params = new URLSearchParams(search);
      const lt = params.get('lt');
      if (!lt) {
        setLinkChecked(true);
        return;
      }

      setLinkChecked(false);
      try {
        try {
          window.sessionStorage.setItem(SHARE_LINK_ONBOARDING_SESSION_KEY, 'true');
        } catch (error) {
          void error;
        }
        await fetch(`/api/links/${encodeURIComponent(lt)}/accept`, { method: 'POST', credentials: 'include' });
        params.delete('lt');
        const newSearch = params.toString();
        const cleanUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash;
        window.history.replaceState({}, '', cleanUrl);
      } catch (error) {
        logger.warn('Failed to accept access link token', error);
      } finally {
        setLinkChecked(true);
      }
    })();
  }, [search]);

  // Extract diagram id from common editor route patterns like /f/:folderId/d/:diagramId
  const diagramMatch = path.match(/\/f\/[^/]+\/d\/([^/]+)/);
  const diagramId = diagramMatch?.[1];
  
  // Only check access after token refresh is complete and we have a diagramId
  const shouldCheckAccess = diagramId && !isRefreshing && linkChecked && (isAuthenticated || hasAttemptedRefresh.current);
  const { data: accessInfo, isLoading: accessLoading } = useDiagramAccess(shouldCheckAccess ? diagramId : undefined);

  // Token refresh effect
  React.useEffect(() => {
    // If returning from auth app with token
    const url = new URL(window.location.href)
    const token = url.searchParams.get('token')
    if (token) {
      try {
        const user = userFromGravityToken(token)
        setAuth(user, token)
        url.searchParams.delete('token')
        window.history.replaceState({}, '', url.toString())
        authenticated.current = true;
        return
      } catch (error) {
        logger.warn('Ignoring invalid auth token in URL', error);
      }
    }

    if (!isAuthenticated && !authenticated.current && !isLoading && !isRefreshing && !hasAttemptedRefresh.current) {
      logger.info('Attempting token refresh with gravity…');
      hasAttemptedRefresh.current = true;
      
      // window.gravity is always set at the bootstrapping.
      setIsRefreshing(true);
      window.gravity.init();
      window.gravity.proxyAuthenticate()
        .then((success: string|false) => {
          if (isLogoutRedirectPending()) return;
          if (success) {
            const newToken = success;
            // Parse token to get user info
            try {
              const user = userFromGravityToken(newToken);
              logger.info('Setting auth with refreshed token');
              setAuth(user, newToken);
            } catch (error) {
              logger.error('Failed to parse refreshed token:', error);
              clearAuth();
            }
          } else {
            logger.warn('Token refresh failed, clearing auth');
            clearAuth();
          }
        })
        .catch((error: unknown) => {
          logger.error('Token refresh failed:', error);
          clearAuth();
        })
        .finally(() => {
          setIsRefreshing(false);
        });
    }
  }, [isAuthenticated, isLoading, isRefreshing, setAuth, clearAuth]);

  // Bypass only for dedicated test route
  if (path.startsWith('/test-canvas')) {
    return <>{children}</>;
  }

  if (isLoading || isRefreshing) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">{isRefreshing ? 'Refreshing token...' : 'Loading...'}</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated && !hasAttemptedRefresh.current) {
    return null;
  }


  // If not authenticated but we are on a diagram route, allow access when a valid access link grants access
  if (!isAuthenticated && diagramId) {
    // While accepting lt or resolving access, show a small loader instead of redirecting
    const params = new URLSearchParams(search);
    if (params.get('lt')) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Preparing access…</p>
          </div>
        </div>
      );
    }
    
    // If we should check access but haven't started yet, show loading
    if (shouldCheckAccess && accessLoading) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Checking access…</p>
          </div>
        </div>
      );
    }
    
    // If we've checked access and have valid access, allow route without login
    if (shouldCheckAccess && accessInfo && accessInfo.role && accessInfo.role !== 'none') {
      return <>{children}</>;
    }
  }

  if (!isAuthenticated && !isRefreshing) {
    if (fallback) {
      return <>{fallback}</>;
    }

    if (redirectToLogin) {
      const returnTo = location.pathname + location.search; /* refers to window.location, react router has a weird bug */
      return <Navigate to="/login" search={{ returnTo }} replace />;
    }

    const returnTo = router.location.pathname + router.location.search;
    return <Navigate to="/login" search={{ returnTo }} replace />;
  }

  return <>{children}</>;
};
